/**
 * Incidentes — o que quebrou, desde quando, o que fazer.
 *
 * Esta tela é olhada por quem opera o bar de leads às 23h, no celular, depois de
 * perceber que "hoje entrou menos gente". As três perguntas dela, nessa ordem, são
 * as três informações de cada item: **o que aconteceu** (frase de gente, não o
 * enum), **desde quando** (carimbo relativo com o exato no `title`, no fuso do
 * workspace) e **o que resolve** (o gesto, com o link para o lugar do problema).
 *
 * Quatro decisões que valem o comentário:
 *
 *  - **É lista, não tabela.** O DESIGN_SYSTEM §8 desenha incidente como item de
 *    lista com faixa de severidade de 3px à esquerda, e o conteúdo aqui é prosa —
 *    espremer duas frases em célula de 36px devolveria a tela ilegível que este
 *    documento proíbe. Grupos e monitoradores são tabela porque são NÚMEROS em
 *    coluna; incidente é um aviso escrito.
 *  - **Um botão visível por item, e ele muda com o estado.** Aberto pede
 *    dono (Reconhecer), reconhecido pede desfecho (Resolver), resolvido não pede
 *    nada. A outra ação, quando existe, mora no "…" (`ActionMenu`) — composição
 *    do UI kit do export (out/2026). O projeto já teve uma tela com cinco `ghost`
 *    idênticos onde o dono do produto não achou a ação: hierarquia é conteúdo.
 *  - **O filtro vai para o SERVIDOR.** Nada de `.filter()` na página: filtrar
 *    depois de paginar devolveria "nenhum aberto" numa página de 20 que por acaso
 *    só tinha resolvidos — e a conclusão errada seria "está tudo bem".
 *  - **Vazio aqui é boa notícia.** "Nenhum incidente aberto" é o estado em que o
 *    produto deveria viver, então sai em `EmptyState kind="zero"`, nunca no
 *    ErrorState. E só depois de a lista CHEGAR: enquanto carrega é skeleton, que
 *    "não sei" e "zero" são coisas diferentes (invariante 8).
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  ActionMenu,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  SeverityBar,
  SkeletonRegion,
  SkeletonText,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTrigger,
  cn,
  useToast,
} from '@mgdlead/ui';
import { ArrowRight, Check, Eye } from 'lucide-react';
import { can } from '@mgdlead/shared';

import { FormAlert } from '@mgdlead/ui';
import { PageHeader } from '@mgdlead/ui';
import {
  DEFAULT_INCIDENT_FILTER,
  INCIDENT_ACTION,
  INCIDENT_ACTION_EXPLAINER,
  INCIDENT_ACTION_HINT,
  INCIDENT_EMPTY,
  INCIDENT_FILTERS,
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
  UNKNOWN_TYPE_ACTION,
  incidentEntityLink,
  incidentTypeSpec,
  isBrokenInviteIncident,
  primaryActionFor,
} from '../../../features/incident/incident-ui';
import type { IncidentAction, IncidentFilter } from '../../../features/incident/incident-ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import {
  useAcknowledgeIncident,
  useIncidentCounts,
  useIncidents,
  useResolveIncident,
} from '../../../lib/api';
import type { Incident } from '../../../lib/api';
import { formatExact, formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import { InviteFixForm } from './InviteFixForm';

/** A frase que ensina o termo. Uma só, usada no cabeçalho da tela. */
const O_QUE_E =
  'Incidente é um problema que o próprio sistema detectou na sua operação: número fora do ar, link sem grupo com vaga, contagem que não bate. Ninguém abre incidente na mão.';

export function IncidentsPage() {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  useDocumentTitle('Incidentes');

  const [filter, setFilter] = useState<IncidentFilter>(DEFAULT_INCIDENT_FILTER);
  const [rowError, setRowError] = useState<string | null>(null);
  /** Qual linha está esperando o servidor — para o botão certo carregar, e só ele. */
  const [pending, setPending] = useState<{ id: string; action: IncidentAction } | null>(null);

  /**
   * Esconder por `can(...)` é UX, não segurança: `incident:acknowledge` é conferido no
   * servidor de qualquer jeito. O que se evita aqui é oferecer um botão que só daria erro
   * — e quem só tem `incident:read` continua vendo a lista inteira, que é o que interessa
   * para saber por que o número do dia caiu.
   */
  const canAct = can(role, 'incident:acknowledge');
  /** D1: trocar o link é trocar o destino de quem clica — `group:update`, como no servidor. */
  const canFixInvite = can(role, 'group:update');

  const listQuery = useIncidents(workspace.id, filter);
  const countsQuery = useIncidentCounts(workspace.id);
  const acknowledge = useAcknowledgeIncident();
  const resolve = useResolveIncident();

  const incidents = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.incidents) ?? [],
    [listQuery.data],
  );

  const counts = countsQuery.data;
  const criticos = counts?.critical ?? 0;

  const run = useCallback(
    (action: IncidentAction, incident: Incident) => {
      setRowError(null);
      setPending({ id: incident.id, action });

      const mutation = action === 'acknowledge' ? acknowledge : resolve;
      mutation.mutate(
        { workspaceId: workspace.id, incidentId: incident.id },
        {
          onSuccess: () =>
            toast({
              title: action === 'acknowledge' ? 'Incidente reconhecido' : 'Incidente resolvido',
              description: INCIDENT_ACTION_HINT[action],
              tone: 'success',
            }),
          onError: (error: unknown) => setRowError(humanizeError(error)),
          onSettled: () => setPending(null),
        },
      );
    },
    [acknowledge, resolve, toast, workspace.id],
  );

  return (
    <Tabs
      variant="segmented"
      value={filter}
      onValueChange={(value) => setFilter(value as IncidentFilter)}
      className="gap-0"
    >
      {/* Composição do UI kit: os filtros com contagem moram no cabeçalho, ao lado do título. */}
      <PageHeader
        title="Incidentes"
        description={O_QUE_E}
        actions={
          <TabsList label="Filtrar incidentes por situação" className="max-w-full overflow-x-auto">
            {INCIDENT_FILTERS.map((option) => (
              <TabsTrigger
                key={option.value}
                value={option.value}
                {...(countFor(option.value, counts) !== undefined
                  ? { count: countFor(option.value, counts) }
                  : {})}
              >
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        }
      />

      {/*
        Crítico é a única severidade que pode gritar (DESIGN_SYSTEM §8). O texto não
        afirma em que situação esses críticos estão: `counts.critical` é um número que o
        servidor manda, e inventar "abertos" em cima dele seria a tela contando uma
        história que não mediu.
      */}
      {criticos > 0 ? (
        <FormAlert tone="danger" className="mb-3">
          {criticos === 1 ? 'Há 1 incidente crítico.' : `Há ${criticos} incidentes críticos.`}{' '}
          {INCIDENT_SEVERITY.CRITICAL.hint}
        </FormAlert>
      ) : null}

      {rowError !== null ? <FormAlert className="mb-3">{rowError}</FormAlert> : null}

      <Card padded={false}>
          {/*
            Um painel só, o do filtro atual: o conteúdo vem do servidor por filtro, então
            manter quatro painéis montados seria guardar três listas velhas na tela.
          */}
          <TabsPanel value={filter}>
            {listQuery.isPending ? (
              <SkeletonRegion
                label="Carregando incidentes"
                className="flex flex-col gap-4 p-3 md:p-4"
              >
                {[0, 1, 2].map((linha) => (
                  <SkeletonText key={linha} lines={3} />
                ))}
              </SkeletonRegion>
            ) : listQuery.isError ? (
              <ErrorState
                title="Não conseguimos carregar os incidentes"
                description={humanizeError(listQuery.error)}
                onRetry={() => void listQuery.refetch()}
              />
            ) : incidents.length === 0 ? (
              <EmptyState
                kind={INCIDENT_EMPTY[filter].kind}
                title={INCIDENT_EMPTY[filter].title}
                description={INCIDENT_EMPTY[filter].description}
              />
            ) : (
              <>
                <ul className="flex flex-col divide-y divide-border">
                  {incidents.map((incident) => (
                    <li key={incident.id}>
                      <IncidentItem
                        incident={incident}
                        slug={workspace.slug}
                        timezone={workspace.timezone}
                        canAct={canAct}
                        canFixInvite={canFixInvite}
                        workspaceId={workspace.id}
                        pending={pending?.id === incident.id ? pending.action : null}
                        onAct={run}
                      />
                    </li>
                  ))}
                </ul>

                {listQuery.hasNextPage ? (
                  <div className="border-t border-border px-4 py-3 md:px-5">
                    <Button
                      variant="ghost"
                      loading={listQuery.isFetchingNextPage}
                      loadingLabel="Carregando"
                      onClick={() => void listQuery.fetchNextPage()}
                      className="max-md:w-full"
                    >
                      Carregar mais
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </TabsPanel>
      </Card>

      {/* A diferença entre os dois gestos, escrita UMA vez, no lugar em que se lê antes de clicar. */}
      <p className="mt-3 max-w-prose text-xs text-subtle-foreground">{INCIDENT_ACTION_EXPLAINER}</p>
    </Tabs>
  );
}

/* ========================================================================== */
/*  Contagem nas abas                                                          */
/* ========================================================================== */

/**
 * `undefined` quando o servidor não contou isso — e "Resolvidos" e "Todos" NÃO têm
 * contagem no contrato (`incident.counts` devolve abertos, reconhecidos e críticos).
 * Inventar um total somando o que está na tela daria "3" numa página de 20 de 47.
 */
function countFor(
  filter: IncidentFilter,
  counts: { open: number; acknowledged: number } | undefined,
): number | undefined {
  if (counts === undefined) return undefined;
  if (filter === 'OPEN') return counts.open;
  if (filter === 'ACKNOWLEDGED') return counts.acknowledged;
  return undefined;
}

/* ========================================================================== */
/*  Um incidente                                                               */
/* ========================================================================== */

interface IncidentItemProps {
  incident: Incident;
  slug: string;
  timezone: string;
  canAct: boolean;
  /** D1: pode colar o link novo do grupo (`group:update`). */
  canFixInvite?: boolean;
  workspaceId?: string;
  /** A ação desta linha que está esperando o servidor, ou `null`. */
  pending: IncidentAction | null;
  onAct: (action: IncidentAction, incident: Incident) => void;
}

function IncidentItem({
  incident,
  slug,
  timezone,
  canAct,
  canFixInvite = false,
  workspaceId,
  pending,
  onAct,
}: IncidentItemProps) {
  const severity = INCIDENT_SEVERITY[incident.severity];
  const status = INCIDENT_STATUS[incident.status];
  const type = incidentTypeSpec(incident.type);
  const link = incidentEntityLink(incident, slug);

  const SeverityIcon = severity.icon;
  const StatusIcon = status.icon;
  const TypeIcon = type?.icon;

  const desde = formatRelative(incident.occurredAt);

  return (
    <div className="flex gap-3.5 px-4 py-4 md:px-5">
      {/* Faixa fina, nunca o cartão inteiro pintado (DESIGN_SYSTEM §12). */}
      <SeverityBar severity={incident.severity} />

      <div className="flex min-w-0 flex-1 flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          {/* Linha de cabeçalho do kit: título forte + severidade + situação + desde quando. */}
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">{incident.title}</h3>
            <Badge tone={severity.tone} icon={<SeverityIcon />} title={severity.hint}>
              {severity.label}
            </Badge>
            <Badge tone={status.tone} icon={<StatusIcon />} title={status.hint}>
              {status.label}
            </Badge>
            {/*
              "Desde quando" é metade da pergunta desta tela. O relativo responde rápido; o
              exato, no `title`, é o que se compara com o horário do anúncio — e sai no fuso
              do WORKSPACE, não no do navegador (invariante 10).
            */}
            <span
              className="text-xs text-subtle-foreground"
              title={formatExact(incident.occurredAt, timezone) ?? undefined}
            >
              Começou {desde ?? 'em data desconhecida'}
            </span>
          </div>

          <p className="max-w-3xl text-sm text-muted-foreground">
            {type?.happened ?? incident.description ?? status.hint}
          </p>

          {/* A descrição do servidor tem o detalhe do caso (qual grupo, quanto divergiu). */}
          {type !== null && incident.description !== null ? (
            <p className="max-w-3xl text-xs text-muted-foreground">{incident.description}</p>
          ) : null}

          <p className="max-w-3xl text-xs">
            <span className="font-medium text-foreground">O que resolve: </span>
            <span className="text-muted-foreground">{type?.action ?? UNKNOWN_TYPE_ACTION}</span>
          </p>

          <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-subtle-foreground">
            {/*
              O NOME do problema, nunca o enum: `MONITOR_OFFLINE` é como a tabela chama
              isso. Tipo que esta versão do painel não conhece simplesmente não ganha
              etiqueta — o título e a descrição que o servidor mandou seguram a linha.
            */}
            {type && TypeIcon ? (
              <span className="inline-flex items-center gap-1">
                <TypeIcon aria-hidden="true" className="size-3.5" />
                {type.label}
              </span>
            ) : null}

            {incident.acknowledgedAt !== null ? (
              <span title={formatExact(incident.acknowledgedAt, timezone) ?? undefined}>
                Reconhecido{' '}
                {incident.acknowledgedByName !== null ? `por ${incident.acknowledgedByName} ` : ''}
                {formatRelative(incident.acknowledgedAt) ?? ''}
              </span>
            ) : null}

            {incident.resolvedAt !== null ? (
              <span title={formatExact(incident.resolvedAt, timezone) ?? undefined}>
                Resolvido {formatRelative(incident.resolvedAt) ?? ''}
              </span>
            ) : null}

            {/*
              O caminho para o lugar do problema. Sem ele, quem lê "grupo sem vaga" tem de
              caçar qual grupo no menu — e este projeto já teve 15 telas sem caminho.
            */}
            {link !== null ? (
              <Link
                to={link.to}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md font-medium text-primary-text underline-offset-2 hover:underline',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'max-md:min-h-11',
                )}
              >
                {link.label}
                <ArrowRight aria-hidden="true" className="size-3" />
              </Link>
            ) : null}
          </span>

          {/*
            D1: convite quebrado se resolve aqui mesmo, colando o link novo. Só para incidente
            de grupo ainda aberto, e só para quem pode trocar o destino de quem clica.
          */}
          {canFixInvite &&
          workspaceId !== undefined &&
          incident.status !== 'RESOLVED' &&
          incident.entityId !== null &&
          isBrokenInviteIncident(incident.type) ? (
            <div className="mt-2">
              <InviteFixForm
                workspaceId={workspaceId}
                groupId={incident.entityId}
                incidentTitle={incident.title}
              />
            </div>
          ) : null}
        </div>

        <IncidentActions incident={incident} canAct={canAct} pending={pending} onAct={onAct} />
      </div>
    </div>
  );
}

/**
 * As duas ações, como no UI kit: UMA visível (a que o estado pede) e a outra no "…".
 *
 * A visível é `neutral` — nenhuma `primary`: a vaga de única cor de ação (DESIGN_SYSTEM §1.5)
 * não pode ser gasta numa LISTA, senão dez incidentes viram dez CTAs disputando entre si.
 * Quem grita a urgência é a faixa de severidade e o aviso vermelho no topo.
 */
function IncidentActions({
  incident,
  canAct,
  pending,
  onAct,
}: Pick<IncidentItemProps, 'incident' | 'canAct' | 'pending' | 'onAct'>) {
  const destaque = primaryActionFor(incident.status);

  if (!canAct || destaque === null) return null;

  const visivel = (action: IncidentAction) => (
    <Button
      variant="neutral"
      size="sm"
      leadingIcon={action === 'acknowledge' ? <Eye /> : <Check />}
      title={INCIDENT_ACTION_HINT[action]}
      loading={pending === action}
      loadingLabel={action === 'acknowledge' ? 'Reconhecendo' : 'Resolvendo'}
      disabled={pending !== null}
      aria-label={`${INCIDENT_ACTION[action]}: ${incident.title}`}
      onClick={() => onAct(action, incident)}
      className="max-md:flex-1"
    >
      {INCIDENT_ACTION[action]}
    </Button>
  );

  return (
    <div className="flex items-center gap-2 max-md:w-full md:shrink-0 md:justify-end">
      {visivel(destaque)}
      {/*
        Aberto: "Reconhecer" na frente e "Resolver" no menu (dá para fechar direto). Reconhecido
        já tem dono: oferecer "Reconhecer" de novo seria um botão que não muda nada — então não
        há menu.
      */}
      {destaque === 'acknowledge' ? (
        <ActionMenu
          label={`Mais ações: ${incident.title}`}
          items={[
            {
              id: 'resolve',
              label: INCIDENT_ACTION.resolve,
              icon: <Check />,
              disabled: pending !== null,
              onSelect: () => onAct('resolve', incident),
            },
          ]}
        />
      ) : null}
    </div>
  );
}
